import React, { createContext, Fragment, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';

// TODO: SHOULD BE PART OF MIX THIS MODULE AND AUTH MICROFRONT
import { JWTContextType, PasswordResetConfirmMutationInput, User } from 'model/types/auth';
import { UserMerchantPreferencesScopeResolutionInput } from 'model/types/user-merchant-preferences';
import { authenticatedPost, setSession, unauthenticatedPost } from 'utils/axios';
import {
  PREFERENCES_FLUSH_TIMEOUT_MS,
  flushUserMerchantPreferences,
  hydrateUserMerchantPreferences,
  resetUserMerchantPreferences
} from 'utils/user-merchant-metadata/userMerchantMetadata';
import { resolveUserMerchantPreferencesScope } from 'utils/userMerchantPreferencesScope';

// state-management
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { merchantAttachStarted, sessionEnded } from 'app/actions/sessionLifecycle';
import { selectSessionStatus } from 'app/selectors/selectSessionStatus';
import { queryMerchantInfo, setBasicInfo } from 'app/slices/merchantSlice';
import { bootSettled, decodeSessionToken, merchantAttached, queryUser, setUser, userLogin } from 'app/slices/authSlice';

// queries
import { USER_REGISTER } from 'queries/user_register';
import { PASSWORD_RESET } from 'queries/password_reset';
import { PASSWORD_RESET_CONFIRM } from 'queries/password_reset_confirm';
import { ATTACH_MERCHANT_TO_SESSION } from 'queries/attatch_merchant_to_session';

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

function hydratePreferences({ user, merchantId }: UserMerchantPreferencesScopeResolutionInput): void {
  const scope = resolveUserMerchantPreferencesScope({ user, merchantId });
  if (scope) {
    hydrateUserMerchantPreferences(scope);
  }
}

export const JWTProvider = ({ children }: { readonly children: React.ReactNode }) => {
  const { isLoggedIn, flags, user, userTokenInfo } = useAppSelector((store) => store.auth);
  const hasCampaignPlan = useAppSelector((store) => store.merchant.hasCampaignPlan);
  const activeMerchantId = useAppSelector((store) => store.merchant.merchant?.id);
  const { status } = useAppSelector(selectSessionStatus);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const init = async () => {
      const token = window.localStorage.getItem('token');
      if (!token) {
        // There is no session to end, so nothing in the store is reset.
        dispatch(bootSettled());
        return;
      }
      try {
        await startSession(token);
      } catch (e) {
        await abandonSession(e);
      }
    };
    init().catch((err) => Sentry.captureException(err));
  }, []);

  useEffect(() => {
    hydratePreferences({ user, merchantId: activeMerchantId });
  }, [user, activeMerchantId]);

  const login = async (email: string, password: string) => {
    const result = await userLogin(email, password);
    const { token } = result;
    if (!result.errors && token) {
      setSession(token);
      try {
        await startSession(token);
      } catch (e) {
        await abandonSession(e);
      }
    }
    return result;
  };

  const logout = async () => {
    try {
      await flushUserMerchantPreferences({ timeoutMs: PREFERENCES_FLUSH_TIMEOUT_MS });
    } catch (err) {
      Sentry.captureException(err);
    }
    resetUserMerchantPreferences();
    setSession(null);
    dispatch(sessionEnded());
  };

  // The user is loaded once, here; a merchant switch re-points the token without asking again.
  async function startSession(token: string) {
    const authUser = await queryUser();
    if (!isUsableSession(token, authUser.user)) {
      await logout();
      return;
    }
    dispatch(setUser({ ...authUser, token }));

    if (decodeSessionToken(token)?.role) {
      await loadMerchant(authUser.user);
      return;
    }

    const roles = authUser.user?.roles ?? [];
    const onlyMerchantId = roles.length === 1 ? roles[0].merchant?.id : undefined;
    if (onlyMerchantId) await attachMerchant(onlyMerchantId, authUser.user);
  }

  // Every path into the active status ends here. Preferences are hydrated before the merchant lands,
  // so a surface that mounts in the same commit reads the stored bag rather than its defaults.
  async function loadMerchant(sessionUser?: User | null) {
    const result = await queryMerchantInfo();
    if (!result.merchant) throw new Error('The merchant could not be loaded');
    hydratePreferences({ user: sessionUser, merchantId: result.merchant.id });
    dispatch(setBasicInfo(result));
  }

  async function abandonSession(error: unknown) {
    Sentry.captureException(error);
    await logout();
  }

  const register = async (email: string, password: string, registrationToken: string | null) => {
    if (!registrationToken) return { errors: [{ message: 'Invalid registration token', path: ['userRegister'] }], userRegister: null };
    const response = await unauthenticatedPost({
      query: USER_REGISTER,
      vars: {
        email,
        password,
        registrationToken
      }
    });
    return response;
  };

  const resetPassword = async (email: string) => {
    let returnUrl = `${import.meta.env.VITE_SELLER_API_URL}reset-password`;
    if (isLoggedIn) {
      returnUrl = `${import.meta.env.VITE_SELLER_API_URL}profiles/account/password`;
    }
    const response = await unauthenticatedPost({
      query: PASSWORD_RESET,
      vars: {
        input: {
          email: email,
          returnUrl
        }
      }
    });
    return response;
  };

  const resetPasswordConfirm = async (input: PasswordResetConfirmMutationInput) => {
    const response = await unauthenticatedPost({
      query: PASSWORD_RESET_CONFIRM,
      vars: { input: input }
    });
    return response;
  };

  async function attachMerchant(merchantId: string, sessionUser?: User | null) {
    // Flushed before anything else: from `setSession` below on, the pending write would be POSTed
    // under the incoming merchant's token against the outgoing merchant's role id, which the backend
    // rejects — losing the user's last change to the merchant they are leaving.
    await flushUserMerchantPreferences({ timeoutMs: PREFERENCES_FLUSH_TIMEOUT_MS });
    dispatch(merchantAttachStarted({ merchantId }));
    try {
      const result = await authenticatedPost({
        query: ATTACH_MERCHANT_TO_SESSION,
        vars: {
          merchantId
        }
      });
      const token: string | undefined = result?.data?.attachMerchantToSession?.token;
      if (!token) {
        await logout();
        return;
      }
      setSession(token);
      dispatch(merchantAttached({ token }));
      // An attach that yields no role would otherwise auto-attach again, forever.
      if (!decodeSessionToken(token)?.role) throw new Error('The attached session carries no role');
      await loadMerchant(sessionUser);
    } catch (e) {
      await abandonSession(e);
    }
  }

  const merchantSwitch = (merchantId: string) => attachMerchant(merchantId, user);

  const authInProgress = status === 'booting' || status === 'attaching';

  const value = useMemo(
    () => ({
      authInProgress,
      isLoggedIn,
      flags,
      hasCampaignPlan,
      login,
      logout,
      merchantSwitch,
      register,
      resetPassword,
      resetPasswordConfirm,
      user,
      userRole: userTokenInfo?.role?.type ?? null
    }),
    [
      authInProgress,
      isLoggedIn,
      flags,
      hasCampaignPlan,
      login,
      logout,
      merchantSwitch,
      register,
      resetPassword,
      resetPasswordConfirm,
      user,
      userTokenInfo
    ]
  );

  // Remounting on a merchant change discards every merchant-scoped feature context's local state.
  return (
    <JWTContext.Provider value={value}>
      <Fragment key={activeMerchantId ?? 'no-merchant'}>{children}</Fragment>
    </JWTContext.Provider>
  );
};

function isUsableSession(token: string, user?: User | null) {
  const issuedByCurrentAuth = !!decodeSessionToken(token)?.iss;
  return issuedByCurrentAuth && (user?.roles?.length ?? 0) > 0;
}

export default JWTContext;
